// @givmo/org-console — the Profile page's STATISTICS choice (Profile Enrichment PE-WEB15; D-PE-32 /
// D-EP-51; where it renders, D-PE-41; every stat off, D-PE-42).
//
// Which of GIVMO'S OWN figures the organization's public page shows under its header, and in what
// order. It is NOT a block: the figures are Givmo's, never the organization's words (D-PE-41), so the
// choice rides the owner read's page-level `statistics` object (beside `blocks`, never inside it) and
// the replace body's optional `statistics` list. A save that leaves the list out keeps the stored
// choice exactly as it is — the backend's cross-version law, which is why the block editor's save
// never sends it.
//
// ══ ENTITY-GENERIC BY CONSTRUCTION ═════════════════════════════════════════════════════════════
//
// The catalog — which stats a class has, each one's kind, the default order — is the SERVER'S data
// (`available`, `default`); the host supplies only the words for each key. This file names no class
// and no stat.
//
// ══ A KEY THIS BUILD HAS NO WORDS FOR IS KEPT, NEVER DROPPED ═══════════════════════════════════
//
// A newer backend may add a stat, and an agent may choose it, before this console learns its words.
// Such a key is never drawn — and it keeps its place in every list this file builds, so a person who
// reorders or switches the stats they CAN see never deletes one they cannot (the block editor's
// "an unknown kind goes back byte-for-byte" rule, applied to the choice).

/** The two kinds of figure a catalog row names. Any other value is a kind this build does not know. */
export type ProfileStatisticKind = 'count' | 'amount';

/** One catalog row: a stat this organization's class can show, and what the public sees for it. */
export interface ProfileStatisticDefinition {
  key: string;
  /** `count` or `amount` — kept as served, so an unknown kind is still carried. */
  kind: string;
  /**
   * The figure a signed-in person outside the organization sees for this stat right now, whether or
   * not the stat is chosen — `null` when they don't see it (`withheld` says why) or the server served
   * none (a backend that predates it: the card then shows no figure, never a zero).
   */
  value: number | null;
  /** Why that person doesn't see the figure (the server's reason), or `null`. */
  withheld: string | null;
}

/** One figure as the public sees it: exactly one of `count` / `amount` is a number. */
export interface ProfileStatisticFigure {
  key: string;
  count: number | null;
  amount: number | null;
}

/** A chosen stat the public does not see right now, and the server's reason (a closed vocabulary). */
export interface ProfileStatisticWithheld {
  key: string;
  reason: string;
}

/** The owner read's `statistics` object, read tolerantly. */
export interface OwnerProfileStatistics {
  /** The stored choice, in order; `null` = none stored (the default applies); `[]` = every stat off. */
  selected: string[] | null;
  /** The catalog's default selection, in order — what the page shows while nothing is stored. */
  defaultKeys: string[];
  /** Every stat the class can show, in the catalog's order. */
  available: ProfileStatisticDefinition[];
  /** What a signed-in person outside the organization sees now, in order (View as public). */
  publicView: ProfileStatisticFigure[];
  /** The chosen stats that person does not see, in order, each with why. */
  withheld: ProfileStatisticWithheld[];
}

/** The reasons the server names for a withheld stat (the backend's closed vocabulary). */
export const STATISTIC_WITHHELD_REASONS = ['not_visible', 'below_floor', 'employees_only'] as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function stringList(value: unknown): string[] | null {
  if (!Array.isArray(value)) return null;
  return value.filter((item): item is string => typeof item === 'string' && item.length > 0);
}

function finiteNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  // The backend serializes a Decimal amount as a JSON string ("1250.00"), as the statistics read does.
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

/** One figure off the wire, or `null` for a row with no key or no value (never drawn as a zero). */
export function readStatisticFigure(raw: unknown): ProfileStatisticFigure | null {
  if (!isRecord(raw) || typeof raw.key !== 'string' || raw.key.length === 0) return null;
  const count = finiteNumber(raw.count);
  const amount = finiteNumber(raw.amount);
  if (count === null && amount === null) return null;
  return { key: raw.key, count, amount };
}

function figures(value: unknown): ProfileStatisticFigure[] {
  if (!Array.isArray(value)) return [];
  return value.map(readStatisticFigure).filter((f): f is ProfileStatisticFigure => f !== null);
}

/**
 * One catalog row: its figure is read only while the server does not withhold it — a withheld row's
 * value is never shown, whatever else the row carries.
 */
function readDefinition(row: Record<string, unknown>): ProfileStatisticDefinition {
  const withheld =
    typeof row.withheld === 'string' && row.withheld.length > 0 ? row.withheld : null;
  const value = withheld === null ? (finiteNumber(row.count) ?? finiteNumber(row.amount)) : null;
  return {
    key: row.key as string,
    kind: typeof row.kind === 'string' ? row.kind : '',
    value,
    withheld,
  };
}

/** A catalog row's figure as the public page draws it ("37", "$48,250"), or `null` for none. */
export function formatDefinitionValue(stat: ProfileStatisticDefinition): string | null {
  if (stat.value === null) return null;
  return stat.kind === 'amount'
    ? formatStatisticAmount(stat.value)
    : formatStatisticCount(stat.value);
}

/**
 * The owner read's `statistics`, or `null` when the page carries none (an owner type whose catalog
 * binds no stats, a backend that predates the choice, or a value this build cannot read).
 */
export function readOwnerStatistics(page: unknown): OwnerProfileStatistics | null {
  if (!isRecord(page) || !isRecord(page.statistics)) return null;
  const raw = page.statistics;
  const available = Array.isArray(raw.available)
    ? raw.available
        .filter(isRecord)
        .filter((row) => typeof row.key === 'string' && row.key.length > 0)
        .map((row) => readDefinition(row))
    : [];
  if (available.length === 0) return null;
  const withheld = Array.isArray(raw.withheld)
    ? raw.withheld
        .filter(isRecord)
        .filter((row) => typeof row.key === 'string' && typeof row.reason === 'string')
        .map((row) => ({ key: row.key as string, reason: row.reason as string }))
    : [];
  return {
    selected: raw.selected === null ? null : stringList(raw.selected),
    defaultKeys: stringList(raw.default) ?? available.map((row) => row.key),
    available,
    publicView: figures(raw.public_view),
    withheld,
  };
}

/** The public read's `statistics.items`, or `null` when the page carries no `statistics` value. */
export function readPublicStatistics(page: unknown): ProfileStatisticFigure[] | null {
  if (!isRecord(page) || !isRecord(page.statistics)) return null;
  return figures(page.statistics.items);
}

/** What the page shows: the stored choice, else the catalog's default. */
export function shownKeys(statistics: OwnerProfileStatistics): string[] {
  return statistics.selected ?? statistics.defaultKeys;
}

/**
 * Switch one stat on or off. ON appends it after the last shown stat (a stat that is switched on
 * joins the end of the order); OFF removes it. Every other key — one this build cannot draw included
 * — keeps its place.
 */
export function toggleKey(shown: readonly string[], key: string, on: boolean): string[] {
  const without = shown.filter((k) => k !== key);
  return on ? [...without, key] : without;
}

/**
 * Move one shown stat a place up or down among the stats this build DRAWS (`drawn`), swapping it with
 * the drawn neighbour; a key it cannot draw never moves and is never skipped over into a new place.
 */
export function moveKey(
  shown: readonly string[],
  key: string,
  delta: -1 | 1,
  drawn: (key: string) => boolean,
): string[] {
  const at = shown.indexOf(key);
  if (at < 0) return [...shown];
  let to = at + delta;
  while (to >= 0 && to < shown.length && !drawn(shown[to]!)) to += delta;
  if (to < 0 || to >= shown.length) return [...shown];
  const next = [...shown];
  next[at] = shown[to]!;
  next[to] = key;
  return next;
}

/** The figures in rows of two — the strip's grid on both apps (a lone last figure fills its row). */
export function statisticRows<T>(items: readonly T[]): T[][] {
  const rows: T[][] = [];
  for (let i = 0; i < items.length; i += 2) rows.push(items.slice(i, i + 2));
  return rows;
}

/**
 * A dollar figure as both apps draw it: US dollars, whole dollars without cents ("$12,450"), cents
 * only when there are some (iOS `PublicProfileContent.formatCurrency`, Android `publicProfileCurrency`).
 */
export function formatStatisticAmount(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
    maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
  }).format(amount);
}

/** A count as the apps draw it: the plain number ("1,204"). */
export function formatStatisticCount(count: number): string {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(count);
}

/** "Donated", "Donated and Matched", "Donations, Donated and Matched". */
export function joinLabels(labels: readonly string[]): string {
  if (labels.length <= 1) return labels[0] ?? '';
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
}
